/**
 * <s-collapse> — shows or hides itself in response to an Invoker Command.
 *
 * A button with commandfor="<this id>" command="--toggle" flips the element's `hidden`
 * state and mirrors it on the button's aria-expanded, so labels can swap with CSS.
 *
 * Attributes:
 *   data-persist — optional sessionStorage key; when set, the collapsed state is
 *                  remembered across page loads for the rest of the browsing session.
 */
class SCollapse extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();

    if (this.#read() === 'collapsed') this.#apply(true);

    this.addEventListener(
      'command',
      (event) => {
        if (event.command !== '--toggle') return;
        this.#apply(!this.hidden);
        this.#write(this.hidden ? 'collapsed' : 'expanded');
      },
      { signal: this.#ac.signal },
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #apply(collapsed) {
    this.hidden = collapsed;
    if (!this.id) return;
    for (const button of document.querySelectorAll(`[commandfor="${CSS.escape(this.id)}"]`)) {
      button.setAttribute('aria-expanded', String(!collapsed));
    }
  }

  #read() {
    const key = this.dataset.persist;
    if (!key) return null;
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  #write(value) {
    const key = this.dataset.persist;
    if (!key) return;
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      // Storage can be unavailable (private mode, blocked cookies); the toggle still works.
    }
  }
}

if (!customElements.get('s-collapse')) customElements.define('s-collapse', SCollapse);
